import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../utils/api";

export const useActivity = () => {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get("/initiatives/activity")
      .then(({ data, hasMore, nextCursor }) => {
        if (cancelled) return;
        setActivity(data);
        setHasMore(hasMore);
        setNextCursor(nextCursor);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load activity");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore || !nextCursor) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    api
      .get(`/initiatives/activity?cursor=${encodeURIComponent(nextCursor)}`)
      .then(({ data, hasMore: more, nextCursor: cursor }) => {
        setActivity((prev) => [...prev, ...data]);
        setHasMore(more);
        setNextCursor(cursor);
      })
      .catch((err) => {
        setLoadMoreError(err.message || "Failed to load more activity");
      })
      .finally(() => {
        setLoadingMore(false);
      });
  }, [hasMore, loadingMore, nextCursor]);

  const clearLoadMoreError = useCallback(() => setLoadMoreError(null), []);

  const states = useMemo(
    () => ({
      loading,
      loadingMore,
      error,
      loadMoreError,
      clearLoadMoreError,
      hasMore,
      loadMore,
    }),
    [
      loading,
      loadingMore,
      error,
      loadMoreError,
      clearLoadMoreError,
      hasMore,
      loadMore,
    ],
  );

  return { activity, states, refresh };
};
